/**
 * Chat Store (Zustand) — Web
 *
 * Persistent state for the AI Chat Assistant.
 * Conversations are saved to localStorage and expire after 7 days.
 * When conversation grows too long, the server compacts older messages
 * into a summary — the client replaces them with a summary message.
 */

import { authAwareFetch } from '@contexts/authHttpUtils';
import type { ChatMessage } from '@parlay-picker/shared/chatTypes';
import {
  type ChatState,
  CONVERSATION_MAX_AGE_MS,
  createChatSlice,
} from '@parlay-picker/shared/stores/chatStoreCore';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type { ChatState };

export const useChatStore = create<ChatState>()(
  persist(
    createChatSlice({
      apiFetch: authAwareFetch,
    }),
    {
      name: 'parlay-picker-chat',
      partialize: (state) => ({
        // Only persist conversation data, not UI state
        messages: state.messages,
        conversationId: state.conversationId,
        quickChips: state.quickChips,
        lastActivityAt: state.lastActivityAt,
      }),
      onRehydrateStorage: () => {
        // Return the post-hydration callback
        return (state, error) => {
          if (error || !state) return;
          // Check if conversation has expired (older than 7 days)
          const age = Date.now() - (state.lastActivityAt || 0);
          if (age > CONVERSATION_MAX_AGE_MS) {
            // Clear expired conversation
            state.messages = [];
            state.conversationId = null;
            state.quickChips = [];
            state.lastActivityAt = Date.now();
          }
        };
      },
    },
  ),
);

// Selector hooks for fine-grained subscriptions
export const useChatIsOpen = (): boolean => useChatStore((s) => s.isOpen);
export const useChatMessages = (): ChatMessage[] => useChatStore((s) => s.messages);
export const useChatIsStreaming = (): boolean => useChatStore((s) => s.isStreaming);
export const useChatUnreadCount = (): number => useChatStore((s) => s.unreadCount);
export const useChatChips = (): string[] => useChatStore((s) => s.quickChips);
export const useChatPendingConfirmation = (): ChatState['pendingConfirmation'] =>
  useChatStore((s) => s.pendingConfirmation);
export const useChatPendingFollowupActive = (): boolean =>
  useChatStore((s) => s.pendingFollowupActive);
